import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { TradeDoc, TradeLine, TradeStatus } from "./tradeProposalTypes";
import type { TradeItem } from "./tradeTypes";
import { computeTotal } from "./tradeHelpers";

function toLine(it: TradeItem): TradeLine {
  return {
    ownerUid: it.ownerUid ?? null,
    docId: it.docId ?? null,
    origin: it.origin,
    scryfall_id: it.scryfall_id ?? null,
    name: it.name,
    set: it.set ?? null,
    number: it.number ?? null,
    image: it.image ?? null,
    foil: Boolean(it.foil),
    qty: Math.max(1, Math.floor(it.qty)),
    unitPrice: typeof it.unitPrice === "number" ? it.unitPrice : null,
  };
}

function totalsFor(proposerSent: TradeItem[], recipientSent: TradeItem[]) {
  const proposerSentTotal = computeTotal(proposerSent);
  const proposerReceivedTotal = computeTotal(recipientSent);
  const recipientSentTotal = proposerReceivedTotal;
  const recipientReceivedTotal = proposerSentTotal;
  return {
    proposer: { sent: proposerSentTotal, received: proposerReceivedTotal, diff: proposerReceivedTotal - proposerSentTotal },
    recipient: { sent: recipientSentTotal, received: recipientReceivedTotal, diff: recipientReceivedTotal - recipientSentTotal },
  };
}

export async function createTradeProposal(params: {
  proposerUid: string;
  recipientUid: string;
  sentByProposer: TradeItem[];
  sentByRecipient: TradeItem[];
  message?: string | null;
}): Promise<string> {
  const { proposerUid, recipientUid, sentByProposer, sentByRecipient, message } = params;
  const ts = serverTimestamp();
  const t = totalsFor(sentByProposer, sentByRecipient);

  const payload: TradeDoc = {
    status: "open",
    proposerUid,
    recipientUid,
    participants: [proposerUid, recipientUid],
    sentByProposer: sentByProposer.map(toLine),
    sentByRecipient: sentByRecipient.map(toLine),
    proposerTotals: t.proposer,
    recipientTotals: t.recipient,
    message: message ?? null,
    createdAt: ts,
    updatedAt: ts,
    acceptedByRecipient: false,
    cancelledBy: null,
    declinedBy: null,
  };

  const ref = await addDoc(collection(db, "trades"), payload as any);
  return ref.id;
}

export async function listMyTrades(uid: string, status?: TradeStatus): Promise<(TradeDoc & { id: string })[]> {
  const ref = collection(db, "trades");
  let q = query(ref, where("participants", "array-contains", uid), orderBy("updatedAt", "desc"), limit(50));
  // Firestore needs a composite index for this query.
  const snap = await getDocs(q);
  const out: (TradeDoc & { id: string })[] = [];
  snap.forEach((d) => {
    const v = d.data() as any;
    if (status && v?.status !== status) return;
    out.push({ ...(v as TradeDoc), id: d.id });
  });
  return out;
}

export async function acceptTrade(uid: string, tradeId: string) {
  const ref = doc(db, "trades", tradeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Trade not found");
  const v = snap.data() as TradeDoc;
  if (uid !== v.recipientUid) throw new Error("Only the recipient can accept");
  if (v.status !== "open") throw new Error("Trade not open");
  await updateDoc(ref, { acceptedByRecipient: true, status: "accepted", updatedAt: serverTimestamp() });
}

export async function declineTrade(uid: string, tradeId: string) {
  const ref = doc(db, "trades", tradeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Trade not found");
  const v = snap.data() as TradeDoc;
  if (uid !== v.recipientUid) throw new Error("Only the recipient can decline");
  if (v.status !== "open") throw new Error("Trade not open");
  await updateDoc(ref, { declinedBy: uid, status: "declined", updatedAt: serverTimestamp() });
}

export async function cancelTrade(uid: string, tradeId: string) {
  const ref = doc(db, "trades", tradeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Trade not found");
  const v = snap.data() as TradeDoc;
  if (uid !== v.proposerUid) throw new Error("Only the proposer can cancel");
  if (v.status !== "open") throw new Error("Trade not open");
  await updateDoc(ref, { cancelledBy: uid, status: "cancelled", updatedAt: serverTimestamp() });
}
